// Static course content for Back Pain Solution — 4 Weeks × 4 Practices

export const STEPS = [
  {
    step: 1,
    slug: "get-real",
    title: "Get Real",
    subtitle: "Awareness · Emotional honesty",
    shift: "My pain may not just be physical.",
    accent: "#0A84FF",
    description:
      "Begin identifying the hidden emotional, mental, and physical patterns contributing to tension and pain. Break denial. Build awareness.",
  },
  {
    step: 2,
    slug: "physical-self",
    title: "My Physical Self",
    subtitle: "Alignment · Breath · Body awareness",
    shift: "My body is not broken. It has adapted.",
    accent: "#5AC8FA",
    description:
      "Reconnect to your body safely and consciously. Notice posture, breath, tension, and the patterns the body has memorized.",
  },
  {
    step: 3,
    slug: "mental-self",
    title: "My Mental Self",
    subtitle: "Thoughts · Identity · Inner dialogue",
    shift: "My thoughts affect my biology.",
    accent: "#34C759",
    description:
      "Interrupt subconscious mental loops reinforcing pain and stress. Notice the programs running in the background of your life.",
  },
  {
    step: 4,
    slug: "mind-body",
    title: "My Mind–Body",
    subtitle: "Integration · Rewiring · Expansion",
    shift: "I can become someone new.",
    accent: "#FF9500",
    description:
      "Unify body, mind, emotion, and nervous system into one coherent identity. Embody who you are becoming.",
  },
];

const scaleOptions = [
  { value: 1, label: "Rarely" },
  { value: 2, label: "Sometimes" },
  { value: 3, label: "Often" },
  { value: 4, label: "Most days" },
  { value: 5, label: "Constantly" },
];

/**
 * Each survey question carries metadata so a single normalization engine can
 * compute consistent scores. See lib/survey.js for the scoring algorithm.
 *
 *   reverse: true  → the statement is "bad". High raw value (Strongly Agree)
 *                    means worse outcome → we flip it (5→0, 1→100).
 *   reverse: false → the statement is "good". High raw = better outcome.
 *
 *   category: "mental" | "physical" | "emotional"
 *      Used to compute the Back Pain Transformation Index (BPTI).
 */
const mkQ = (idPrefix, items) =>
  items.map((q, i) => ({
    id: `${idPrefix}q${i + 1}`,
    text: q.text,
    type: "scale",
    options: scaleOptions,
    reverse: q.reverse !== false,
    category: q.category,
  }));

export const SURVEYS = {
  1: {
    title: "Where Am I Really?",
    subtitle: "A 10-question emotional & mental baseline.",
    questions: mkQ("s1", [
      { text: "I feel a steady undercurrent of stress.", category: "emotional", reverse: true },
      { text: "I am living with hidden fear or anxiety.", category: "mental", reverse: true },
      { text: "I quietly carry resentment toward people or events.", category: "emotional", reverse: true },
      { text: "I feel burnt out by my responsibilities.", category: "emotional", reverse: true },
      { text: "I suppress emotions to keep moving.", category: "emotional", reverse: true },
      { text: "I feel hopeless about my body or my pain.", category: "mental", reverse: true },
      { text: "I scan my environment for threat (hypervigilance).", category: "mental", reverse: true },
      { text: "I take on too much for others.", category: "emotional", reverse: true },
      { text: "I feel exhausted by who I have to be each day.", category: "emotional", reverse: true },
      { text: "I sense my body is holding what I cannot say.", category: "mental", reverse: true },
    ]),
  },
  2: {
    title: "How Am I Treating My Body?",
    subtitle: "Physical identity & lifestyle awareness.",
    questions: mkQ("s2", [
      { text: "I spend most of my day seated.", category: "physical", reverse: true },
      { text: "My sleep is restless or insufficient.", category: "physical", reverse: true },
      { text: "I move my body intentionally each day.", category: "physical", reverse: false },
      { text: "I notice I hold my breath when stressed.", category: "physical", reverse: true },
      { text: "I am aware of my posture during the day.", category: "physical", reverse: false },
      { text: "I feel confident in how my body looks and moves.", category: "physical", reverse: false },
      { text: "I clench, brace, or tense without noticing.", category: "physical", reverse: true },
      { text: "I feel low energy by the afternoon.", category: "physical", reverse: true },
      { text: "I walk regularly and freely.", category: "physical", reverse: false },
      { text: "I notice tension patterns in my neck, back, or hips.", category: "physical", reverse: true },
    ]),
  },
  3: {
    title: "What Programs Are Running Me?",
    subtitle: "Mental loops, identity, and inner dialogue.",
    questions: mkQ("s3", [
      { text: "I catastrophize when something goes wrong.", category: "mental", reverse: true },
      { text: "I am hard on myself.", category: "mental", reverse: true },
      { text: "I struggle with perfectionism.", category: "mental", reverse: true },
      { text: "I think from a scarcity mindset.", category: "mental", reverse: true },
      { text: "I fear failure.", category: "mental", reverse: true },
      { text: "I fear being judged.", category: "mental", reverse: true },
      { text: "I try to control everything around me.", category: "mental", reverse: true },
      { text: "I run negative self-talk.", category: "mental", reverse: true },
      { text: "I feel mentally overwhelmed.", category: "mental", reverse: true },
      { text: "I keep emotions hidden to stay functional.", category: "emotional", reverse: true },
    ]),
  },
  4: {
    title: "Who Am I Becoming?",
    subtitle: "Integrated self — resilience, alignment, future.",
    questions: mkQ("s4", [
      { text: "I feel emotionally resilient.", category: "emotional", reverse: false },
      { text: "I am more self-aware than before.", category: "mental", reverse: false },
      { text: "I feel confident in my physical body.", category: "physical", reverse: false },
      { text: "I feel hopeful about the future.", category: "emotional", reverse: false },
      { text: "I feel calm in my nervous system.", category: "mental", reverse: false },
      { text: "I have steady, usable energy.", category: "physical", reverse: false },
      { text: "I feel connected to my body.", category: "physical", reverse: false },
      { text: "I feel mentally confident.", category: "mental", reverse: false },
      { text: "I can clearly see who I am becoming.", category: "mental", reverse: false },
      { text: "My pain has reduced.", category: "physical", reverse: false },
    ]),
  },
};

export const JOURNAL_PROMPTS = {
  1: {
    title: "What Have I Been Carrying?",
    intro:
      "Move slowly. Let yourself be honest. Nothing here is graded — only revealed.",
    prompts: [
      "What anger, regret, or fear have I buried?",
      "Where am I people-pleasing or over-responsible?",
      "What childhood conditioning still shapes me?",
      "Which emotion is heaviest right now?",
    ],
  },
  2: {
    title: "What Physical Behaviors Reinforce My Pain?",
    intro: "Notice the body's hidden choreography of stress.",
    prompts: [
      "When during the day do I tense up?",
      "When do I collapse or shrink physically?",
      "What situations drain my body the most?",
      "What posture do I carry under pressure?",
    ],
  },
  3: {
    title: "What Story Have I Been Living Inside?",
    intro: "Each thought is a small program. Inspect them.",
    prompts: [
      "What do I fear most?",
      "What identity am I protecting?",
      "What belief keeps repeating in my mind?",
      "Who would I become if I healed?",
    ],
  },
  4: {
    title: "Who Do I Choose to Become?",
    intro: "Speak from the future self that is already forming.",
    prompts: [
      "What does my future self feel like in the body?",
      "How does my body move now?",
      "What energy do I carry?",
      "What am I ready to release forever?",
    ],
  },
};

export const EMOTION_PAIRS = [
  { emotion: "Fear", opposite: "Safety" },
  { emotion: "Shame", opposite: "Acceptance" },
  { emotion: "Helplessness", opposite: "Strength" },
  { emotion: "Anger", opposite: "Peace" },
  { emotion: "Resentment", opposite: "Release" },
  { emotion: "Anxiety", opposite: "Calm" },
  { emotion: "Sadness", opposite: "Hope" },
  { emotion: "Loneliness", opposite: "Connection" },
  { emotion: "Guilt", opposite: "Self-forgiveness" },
  { emotion: "Overwhelm", opposite: "Clarity" },
];

// Body regions — each maps to an SVG element id in <BodyPainScanner/>
export const BODY_REGIONS = {
  front: [
    { id: "neck-front", label: "Neck" },
    { id: "jaw", label: "Jaw" },
    { id: "trap-l", label: "Left Trapezius" },
    { id: "trap-r", label: "Right Trapezius" },
    { id: "shoulder-l", label: "Left Shoulder" },
    { id: "shoulder-r", label: "Right Shoulder" },
    { id: "chest", label: "Chest" },
    { id: "abdomen", label: "Abdomen" },
    { id: "hip-l", label: "Left Hip" },
    { id: "hip-r", label: "Right Hip" },
    { id: "thigh-l", label: "Left Thigh" },
    { id: "thigh-r", label: "Right Thigh" },
    { id: "knee-l", label: "Left Knee" },
    { id: "knee-r", label: "Right Knee" },
  ],
  back: [
    { id: "neck-back", label: "Neck (back)" },
    { id: "trap-l-back", label: "Left Trapezius (back)" },
    { id: "trap-r-back", label: "Right Trapezius (back)" },
    { id: "upper-back", label: "Upper Back" },
    { id: "mid-back", label: "Mid Back" },
    { id: "lower-back", label: "Lower Back" },
    { id: "glute-l", label: "Left Glute" },
    { id: "glute-r", label: "Right Glute" },
    { id: "hamstring-l", label: "Left Hamstring" },
    { id: "hamstring-r", label: "Right Hamstring" },
    { id: "calf-l", label: "Left Calf" },
    { id: "calf-r", label: "Right Calf" },
  ],
};

export function intensityColor(intensity) {
  if (intensity <= 0) return "transparent";
  if (intensity <= 3) return "#0A84FF";
  if (intensity <= 6) return "#FFCC00";
  if (intensity <= 8) return "#FF9500";
  return "#FF3B30";
}

export function intensityLabel(intensity) {
  if (intensity <= 0) return "None";
  if (intensity <= 3) return "Low";
  if (intensity <= 6) return "Moderate";
  if (intensity <= 8) return "High";
  return "Severe";
}

// ----- 7-day Practice Exercise videos (per week) -----
// Lightweight placeholder library. The coach can swap URLs anytime in the admin panel
// (a future update will surface this in the UI; backend already allows admin to upsert
// videos per step — daily_videos can ride on the same channel).
const PLACEHOLDER_EMBED = [
  "https://www.youtube.com/embed/inpok4MKVLM",
  "https://www.youtube.com/embed/aXItOY0sLRY",
  "https://www.youtube.com/embed/86m4RC_ADEY",
  "https://www.youtube.com/embed/ZToicYcHIOU",
  "https://www.youtube.com/embed/v7AYKMP6rOE",
  "https://www.youtube.com/embed/4pKly2JojMw",
  "https://www.youtube.com/embed/sTANio_2E0Q",
];

function buildDays(weekNum, themes) {
  return themes.map((t, i) => ({
    day: i + 1,
    title: `Day ${i + 1} — ${t}`,
    url: PLACEHOLDER_EMBED[i % PLACEHOLDER_EMBED.length],
    duration: ["6 min","8 min","7 min","10 min","9 min","8 min","12 min"][i],
  }));
}

export const DAILY_VIDEOS = {
  1: buildDays(1, [
    "Notice without fixing",
    "Body scan baseline",
    "Breath as anchor",
    "Soften the jaw and shoulders",
    "Find the emotion underneath",
    "Sit with what's loud",
    "Close the week with awareness",
  ]),
  2: buildDays(2, [
    "Wake up the spine",
    "Open the chest",
    "Reset the hips",
    "Lengthen the back of the body",
    "Strengthen the deep core",
    "Posture under load",
    "Glide and integrate",
  ]),
  3: buildDays(3, [
    "Catch the thought",
    "Rewrite the inner sentence",
    "Belief inventory",
    "Worst-case → best-case rehearsal",
    "Identity in the mirror",
    "Watch the story without joining it",
    "Future-self embodiment",
  ]),
  4: buildDays(4, [
    "Three-circle check-in (mind/body/emotion)",
    "Daily ritual lockdown",
    "Movement as medicine",
    "Co-regulate with breath + posture",
    "Wear the Back Pain Transformer with intention",
    "Ground in the new identity",
    "Graduation day — full integration",
  ]),
};

// ----- Survey assessment -----
// All bands now interpret the NORMALIZED score (0..100 where 100 = best).
// Bands are listed from low → high — first match by `max` wins.

const STEP_BANDS = {
  1: [
    { max: 25, label: "Critical Load", color: "#EF4444", headline: "Your nervous system is near capacity.", body: "What you've been carrying is real and it is heavy. The work ahead is precisely designed for this." },
    { max: 45, label: "Heavy Load", color: "#F59E0B", headline: "Significant weight is being stored.", body: "Pain often lives where emotion can't. You're in the right place to release what your body is holding." },
    { max: 70, label: "Active Load", color: "#38BDF8", headline: "Real tension is moving through you.", body: "Your nervous system is processing daily friction. The reset begins by giving it a name." },
    { max: 100, label: "Subtle Load", color: "#22D3EE", headline: "Your emotional baseline is calm.", body: "You're carrying less hidden tension than most. This program will refine awareness you already have." },
  ],
  2: [
    { max: 25, label: "Body in Shutdown", color: "#EF4444", headline: "Your body has been protecting itself for a long time.", body: "What you call 'pain' is often armor. We will help it stand down." },
    { max: 45, label: "Disconnected Patterns", color: "#F59E0B", headline: "Your body has adapted in ways you haven't seen.", body: "Collapse, brace, bracing-collapse — patterns are running underneath. Awareness is the first reset." },
    { max: 70, label: "Partial Reconnection", color: "#38BDF8", headline: "There are blind spots in the body.", body: "Tension hides in habits. We will surface them gently." },
    { max: 100, label: "Body Aware", color: "#22D3EE", headline: "Your body and you are in conversation.", body: "Posture, breath, movement — you notice them. We will deepen this." },
  ],
  3: [
    { max: 25, label: "Mental Overload", color: "#EF4444", headline: "Your mind is exhausted from rehearsing pain.", body: "You can change the channel. Neuroplasticity is on your side." },
    { max: 45, label: "Loud Loops", color: "#F59E0B", headline: "Your mind is busy protecting an old identity.", body: "Perfectionism, self-criticism, control — these are nervous-system strategies, not character flaws." },
    { max: 70, label: "Active Loops", color: "#38BDF8", headline: "A few mental loops are running you.", body: "Stories repeat. We will name them and interrupt them." },
    { max: 100, label: "Clear Mind", color: "#22D3EE", headline: "Your inner dialogue runs cleanly.", body: "You catch your thoughts before they catch you. We will sharpen this." },
  ],
  4: [
    { max: 30, label: "Still Integrating", color: "#38BDF8", headline: "You are still becoming.", body: "Integration is not a finish line. Keep going. Each repetition installs the new identity." },
    { max: 55, label: "Reconstructing", color: "#22D3EE", headline: "Significant change is underway.", body: "You can feel parts of you that were locked. Trust the new pattern." },
    { max: 80, label: "Reset State", color: "#10B981", headline: "You are operating from a different nervous system.", body: "What used to flare now flows. Stabilize this." },
    { max: 100, label: "Fully Rewritten", color: "#10B981", headline: "You are no longer the person who arrived here.", body: "Welcome to the Reset State. The Back Pain Transformer and your daily ritual hold this in." },
  ],
};

// Given a normalized 0..100 score and the answer payload (which now carries
// `reverse` + `category` per item), produce: band, top concerns (worst normalized
// outcomes), and per-category averages.
export function assessSurvey(step, score, answers = []) {
  const bands = STEP_BANDS[step] || STEP_BANDS[1];
  const band = bands.find((b) => score <= b.max) || bands[bands.length - 1];

  // Concerns = questions with the LOWEST normalized score (worst outcome).
  // Forward-worded (reverse:false): raw=1 → worst → normalized=0
  // Reverse-worded (reverse:true):  raw=5 → worst → normalized=0
  const withNorm = (answers || [])
    .filter((a) => typeof a.value === "number")
    .map((a) => {
      const v = a.value;
      const fwdNorm = ((v - 1) / 4) * 100;
      const norm = a.reverse ? 100 - fwdNorm : fwdNorm;
      return { text: a.text, value: v, norm, reverse: !!a.reverse, category: a.category };
    });

  const concerns = withNorm
    .filter((a) => a.norm <= 25) // bottom quartile only — true "pressing patterns"
    .sort((a, b) => a.norm - b.norm)
    .slice(0, 3);

  // Per-category averages
  const buckets = { mental: [], physical: [], emotional: [] };
  for (const a of withNorm) {
    if (a.category && buckets[a.category]) buckets[a.category].push(a.norm);
  }
  const avg = (arr) => (arr.length === 0 ? null : Math.round(arr.reduce((x, y) => x + y, 0) / arr.length));
  const categories = {
    mental: avg(buckets.mental),
    physical: avg(buckets.physical),
    emotional: avg(buckets.emotional),
  };

  return { band, concerns, score, categories };
}
